/**
 * Multi-format date and time parser for travel documents
 */

const MONTHS = {
  'jan': 0, 'january': 0,
  'feb': 1, 'february': 1,
  'mar': 2, 'march': 2,
  'apr': 3, 'april': 3,
  'may': 4,
  'jun': 5, 'june': 5,
  'jul': 6, 'july': 6,
  'aug': 7, 'august': 7,
  'sep': 8, 'sept': 8, 'september': 8,
  'oct': 9, 'october': 9,
  'nov': 10, 'november': 10,
  'dec': 11, 'december': 11
};

const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Parse a date string in various formats
 * Returns ISO date string (YYYY-MM-DD) or null
 */
export const parseDate = (dateStr, referenceDate = new Date()) => {
  if (!dateStr) return null;

  const normalized = dateStr.trim().toLowerCase();

  // Try ISO format first: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return dateStr.slice(0, 10);
  }

  // Try US format: MM/DD/YYYY or M/D/YYYY
  const usMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (usMatch) {
    let [, month, day, year] = usMatch;
    year = year.length === 2 ? (parseInt(year) > 50 ? '19' + year : '20' + year) : year;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  // Try European format: DD/MM/YYYY (when day > 12)
  const euMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (euMatch) {
    const [, first, second, year] = euMatch;
    if (parseInt(first) > 12) {
      const yearFull = year.length === 2 ? (parseInt(year) > 50 ? '19' + year : '20' + year) : year;
      return `${yearFull}-${second.padStart(2, '0')}-${first.padStart(2, '0')}`;
    }
  }

  // Try "Dec 15, 2025" or "December 15, 2025"
  const monthDayYearMatch = normalized.match(/^([a-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?,?\s*(\d{4})?$/);
  if (monthDayYearMatch) {
    const [, monthStr, day, year] = monthDayYearMatch;
    const month = MONTHS[monthStr];
    if (month !== undefined) {
      const yearNum = year ? parseInt(year) : inferYear(month, parseInt(day), referenceDate);
      return `${yearNum}-${String(month + 1).padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  }

  // Try "15 Dec 2025" or "15 December 2025"
  const dayMonthYearMatch = normalized.match(/^(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]+),?\s*(\d{4})?$/);
  if (dayMonthYearMatch) {
    const [, day, monthStr, year] = dayMonthYearMatch;
    const month = MONTHS[monthStr];
    if (month !== undefined) {
      const yearNum = year ? parseInt(year) : inferYear(month, parseInt(day), referenceDate);
      return `${yearNum}-${String(month + 1).padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  }

  // Try with day of week prefix: "Sat Dec 15" or "Saturday, December 15, 2025"
  const dowMatch = normalized.match(/^(?:sun|mon|tue|wed|thu|fri|sat)[a-z]*,?\s+([a-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?,?\s*(\d{4})?$/);
  if (dowMatch) {
    const [, monthStr, day, year] = dowMatch;
    const month = MONTHS[monthStr];
    if (month !== undefined) {
      const yearNum = year ? parseInt(year) : inferYear(month, parseInt(day), referenceDate);
      return `${yearNum}-${String(month + 1).padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  }

  // Try "Dec 15" without year
  const monthDayMatch = normalized.match(/^([a-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?$/);
  if (monthDayMatch) {
    const [, monthStr, day] = monthDayMatch;
    const month = MONTHS[monthStr];
    if (month !== undefined) {
      const yearNum = inferYear(month, parseInt(day), referenceDate);
      return `${yearNum}-${String(month + 1).padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  }

  // Try "15 Dec" without year
  const dayMonthMatch = normalized.match(/^(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]+)$/);
  if (dayMonthMatch) {
    const [, day, monthStr] = dayMonthMatch;
    const month = MONTHS[monthStr];
    if (month !== undefined) {
      const yearNum = inferYear(month, parseInt(day), referenceDate);
      return `${yearNum}-${String(month + 1).padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  }

  return null;
};

/**
 * Infer the year when not provided
 * Uses reference date and assumes future travel
 */
const inferYear = (month, day, referenceDate) => {
  const refYear = referenceDate.getFullYear();
  const refMonth = referenceDate.getMonth();
  const refDay = referenceDate.getDate();

  // If the date is in the past for this year, use next year
  if (month < refMonth || (month === refMonth && day < refDay)) {
    return refYear + 1;
  }
  return refYear;
};

/**
 * Parse a time string in various formats
 * Returns 24-hour time string (HH:MM) or null
 */
export const parseTime = (timeStr) => {
  if (!timeStr) return null;

  const normalized = timeStr.trim().toLowerCase();

  // Try 24-hour format: HH:MM or HH:MM:SS
  const time24Match = normalized.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (time24Match && !normalized.includes('am') && !normalized.includes('pm')) {
    const [, hours, minutes] = time24Match;
    const h = parseInt(hours);
    if (h >= 0 && h <= 23) {
      return `${hours.padStart(2, '0')}:${minutes}`;
    }
  }

  // Try 12-hour format with AM/PM
  const time12Match = normalized.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm|a\.m\.|p\.m\.)$/);
  if (time12Match) {
    let [, hours, minutes, period] = time12Match;
    let h = parseInt(hours);
    const isPM = period.startsWith('p');

    if (h === 12) {
      h = isPM ? 12 : 0;
    } else if (isPM) {
      h += 12;
    }

    return `${String(h).padStart(2, '0')}:${minutes}`;
  }

  // Try just "10am" or "2pm" format
  const simpleMatch = normalized.match(/^(\d{1,2})\s*(am|pm|a\.m\.|p\.m\.)$/);
  if (simpleMatch) {
    let [, hours, period] = simpleMatch;
    let h = parseInt(hours);
    const isPM = period.startsWith('p');

    if (h === 12) {
      h = isPM ? 12 : 0;
    } else if (isPM) {
      h += 12;
    }

    return `${String(h).padStart(2, '0')}:00`;
  }

  return null;
};

/**
 * Extract all dates from a text string
 */
export const extractDates = (text, referenceDate = new Date()) => {
  if (!text) return [];

  const dates = [];
  const patterns = [
    // ISO format
    /\b(\d{4}-\d{2}-\d{2})\b/g,
    // US format
    /\b(\d{1,2}\/\d{1,2}\/\d{2,4})\b/g,
    // Month Day, Year
    /\b((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,2}(?:st|nd|rd|th)?,?\s*\d{0,4})\b/gi,
    // Day Month Year
    /\b(\d{1,2}(?:st|nd|rd|th)?\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*,?\s*\d{0,4})\b/gi,
    // With day of week
    /\b((?:Sun|Mon|Tue|Wed|Thu|Fri|Sat)[a-z]*,?\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,2}(?:st|nd|rd|th)?,?\s*\d{0,4})\b/gi
  ];

  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const parsed = parseDate(match[1], referenceDate);
      if (parsed && !dates.includes(parsed)) {
        dates.push(parsed);
      }
    }
  }

  // Sort chronologically
  dates.sort();
  return dates;
};

/**
 * Extract all times from a text string
 */
export const extractTimes = (text) => {
  if (!text) return [];

  const times = [];
  const patterns = [
    // 24-hour format
    /\b(\d{1,2}:\d{2}(?::\d{2})?)\b/g,
    // 12-hour format with AM/PM
    /\b(\d{1,2}:\d{2}(?::\d{2})?\s*(?:am|pm|a\.m\.|p\.m\.))\b/gi,
    // Simple format like "10am"
    /\b(\d{1,2}\s*(?:am|pm|a\.m\.|p\.m\.))\b/gi
  ];

  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const parsed = parseTime(match[1]);
      if (parsed && !times.includes(parsed)) {
        times.push(parsed);
      }
    }
  }

  return times;
};

/**
 * Combine date and time into ISO datetime
 */
export const combineDateTime = (date, time) => {
  if (!date) return null;
  if (!time) return date;
  return `${date}T${time}:00`;
};

/**
 * Format a date for display
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

/**
 * Format a time for display
 */
export const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

export default {
  parseDate,
  parseTime,
  extractDates,
  extractTimes,
  combineDateTime,
  formatDate,
  formatTime
};
